import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Icon, Input } from 'Components';
import { useReportFilters } from '../../context/ReportFilters.context';
import {
	FILTER_DEFINITIONS,
	SEARCH_PLACEHOLDER
} from '../../TrainingReports.constants';
import {
	FilterDefinition,
	FilterOptionsByKey,
	FilterRow
} from '../../TrainingReports.types';
import FilterDropdown from './FilterDropdown';
import styles from './ReportFilters.module.scss';
import { useTranslation } from 'react-i18next';
import { pluralLabel } from 'LearningManager/helpers/role';

interface ReportFiltersProps {
	options?: FilterOptionsByKey;
	searchPlaceholder?: string;
	className?: string;
}

const PRIMARY_FILTERS = FILTER_DEFINITIONS.filter(
	(definition) => definition.row === FilterRow.Primary
);
const MORE_FILTERS = FILTER_DEFINITIONS.filter(
	(definition) => definition.row === FilterRow.More
);

const ReportFilters: React.FC<ReportFiltersProps> = ({
	options = {},
	searchPlaceholder = SEARCH_PLACEHOLDER,
	className
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const {
		selection,
		search,
		setSearch,
		toggleValue,
		selectValue,
		clearFilter,
		clearAll,
		activeFilterCount,
		moreFiltersCount,
		hasActiveFilters
	} = useReportFilters();
	const { t } = useTranslation();

	const renderFilter = (definition: FilterDefinition) => (
		<React.Fragment key={definition.key}>
			{definition.dividerBefore && <span className={styles.divider} />}
			<FilterDropdown
				definition={definition}
				options={options[definition.key] ?? definition.options ?? []}
				selected={selection[definition.key]}
				onToggle={(value) => toggleValue(definition.key, value)}
				onSelect={(value) => selectValue(definition.key, value)}
				onClear={() => clearFilter(definition.key)}
			/>
		</React.Fragment>
	);

	const clearLabel = useMemo(() => t(pluralLabel(`learning.report.filterDefinitions.clearFilters`, activeFilterCount), { count: activeFilterCount }), [activeFilterCount]);

	return (
		<div className={classNames(styles.root, className)}>
			<div className={styles.row}>
				<Input
					className={styles.search}
					placeholder={t(searchPlaceholder)}
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					prefix={<Icon type="search" role="img" />}
				/>

				{PRIMARY_FILTERS.map(renderFilter)}

				<span className={styles.divider} />

				<button
					type="button"
					className={classNames(styles.control, styles.moreFilters, {
						[styles['control--active']]: moreFiltersCount > 0
					})}
					aria-expanded={isExpanded}
					onClick={() => setIsExpanded((expanded) => !expanded)}
				>
					<Icon
						type="moreFilterIcon"
						role="img"
						className={styles.triggerIcon}
					/>
					<span className={styles.triggerLabel}>{t('learning.report.filterDefinitions.moreFilter')}</span>
					{moreFiltersCount > 0 && (
						<span className={styles.counter}>
							{moreFiltersCount}
						</span>
					)}
					<Icon
						type={isExpanded ? 'chevronUp' : 'chevronDown'}
						role="img"
						className={styles.chevron}
					/>
				</button>

				{hasActiveFilters && (
					<button
						type="button"
						className={styles.clearAll}
						onClick={clearAll}
					>
						<Icon type="filterOutline" role="img" />
						{clearLabel}
					</button>
				)}
			</div>

			{isExpanded && (
				<div className={classNames(styles.row, styles['row--more'])}>
					{MORE_FILTERS.map(renderFilter)}
				</div>
			)}
		</div>
	);
};

export default ReportFilters;
